import React, { useState, useMemo, useRef, useEffect } from "react";
import { Row, Col, Spinner } from "react-bootstrap";
import {
  Plus, X, AlertTriangle, Globe, Phone, MessageSquare, Copy, ChevronDown,
  Upload, Image as ImageIcon, FileText, Video,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  AppButton, AppCard, AppCardContent, DropZone, IconButton, Input, NativeSelect,
  Textarea, FormField, Label,
} from "../ui";
import TemplatePreview from "./TemplatePreview";

const CATEGORIES = [
  { value: "UTILITY", label: "Utility", desc: "Send account updates, order updates, alerts" },
  { value: "MARKETING", label: "Marketing", desc: "Send promotions, offers, recommendations" },
  { value: "AUTHENTICATION", label: "Authentication", desc: "Send OTPs for identity verification" },
];

const LANGUAGES = [
  { value: "af", label: "Afrikaans" },
  { value: "sq", label: "Albanian" },
  { value: "ar", label: "Arabic" },
  { value: "ar_EG", label: "Arabic (EGY)" },
  { value: "ar_AE", label: "Arabic (UAE)" },
  { value: "ar_LB", label: "Arabic (LBN)" },
  { value: "ar_MA", label: "Arabic (MAR)" },
  { value: "ar_QA", label: "Arabic (QAT)" },
  { value: "az", label: "Azerbaijani" },
  { value: "be_BY", label: "Belarusian" },
  { value: "bn", label: "Bengali" },
  { value: "bn_IN", label: "Bengali (IND)" },
  { value: "bg", label: "Bulgarian" },
  { value: "ca", label: "Catalan" },
  { value: "zh_CN", label: "Chinese (CHN)" },
  { value: "zh_HK", label: "Chinese (HKG)" },
  { value: "zh_TW", label: "Chinese (TAI)" },
  { value: "hr", label: "Croatian" },
  { value: "cs", label: "Czech" },
  { value: "da", label: "Danish" },
  { value: "prs_AF", label: "Dari" },
  { value: "nl", label: "Dutch" },
  { value: "nl_BE", label: "Dutch (BEL)" },
  { value: "en", label: "English" },
  { value: "en_GB", label: "English (UK)" },
  { value: "en_US", label: "English (US)" },
  { value: "en_AE", label: "English (UAE)" },
  { value: "en_AU", label: "English (AUS)" },
  { value: "en_CA", label: "English (CAN)" },
  { value: "en_GH", label: "English (GHA)" },
  { value: "en_IE", label: "English (IRL)" },
  { value: "en_IN", label: "English (IND)" },
  { value: "en_JM", label: "English (JAM)" },
  { value: "en_MY", label: "English (MYS)" },
  { value: "en_NZ", label: "English (NZL)" },
  { value: "en_QA", label: "English (QAT)" },
  { value: "en_SG", label: "English (SGP)" },
  { value: "en_UG", label: "English (UGA)" },
  { value: "en_ZA", label: "English (ZAF)" },
  { value: "et", label: "Estonian" },
  { value: "fil", label: "Filipino" },
  { value: "fi", label: "Finnish" },
  { value: "fr", label: "French" },
  { value: "fr_BE", label: "French (BEL)" },
  { value: "fr_CA", label: "French (CAN)" },
  { value: "fr_CH", label: "French (CHE)" },
  { value: "fr_CI", label: "French (CIV)" },
  { value: "fr_MA", label: "French (MAR)" },
  { value: "ka", label: "Georgian" },
  { value: "de", label: "German" },
  { value: "de_AT", label: "German (AUT)" },
  { value: "de_CH", label: "German (CHE)" },
  { value: "el", label: "Greek" },
  { value: "gu", label: "Gujarati" },
  { value: "ha", label: "Hausa" },
  { value: "he", label: "Hebrew" },
  { value: "hi", label: "Hindi" },
  { value: "hu", label: "Hungarian" },
  { value: "id", label: "Indonesian" },
  { value: "ga", label: "Irish" },
  { value: "it", label: "Italian" },
  { value: "ja", label: "Japanese" },
  { value: "kn", label: "Kannada" },
  { value: "kk", label: "Kazakh" },
  { value: "rw_RW", label: "Kinyarwanda" },
  { value: "ko", label: "Korean" },
  { value: "ky_KG", label: "Kyrgyz" },
  { value: "lo", label: "Lao" },
  { value: "lv", label: "Latvian" },
  { value: "lt", label: "Lithuanian" },
  { value: "mk", label: "Macedonian" },
  { value: "ms", label: "Malay" },
  { value: "ml", label: "Malayalam" },
  { value: "mr", label: "Marathi" },
  { value: "nb", label: "Norwegian" },
  { value: "ps_AF", label: "Pashto" },
  { value: "fa", label: "Persian" },
  { value: "pl", label: "Polish" },
  { value: "pt_BR", label: "Portuguese (BR)" },
  { value: "pt_PT", label: "Portuguese (POR)" },
  { value: "pa", label: "Punjabi" },
  { value: "ro", label: "Romanian" },
  { value: "ru", label: "Russian" },
  { value: "sr", label: "Serbian" },
  { value: "si_LK", label: "Sinhala" },
  { value: "sk", label: "Slovak" },
  { value: "sl", label: "Slovenian" },
  { value: "es", label: "Spanish" },
  { value: "es_AR", label: "Spanish (ARG)" },
  { value: "es_CL", label: "Spanish (CHL)" },
  { value: "es_CO", label: "Spanish (COL)" },
  { value: "es_CR", label: "Spanish (CRI)" },
  { value: "es_DO", label: "Spanish (DOM)" },
  { value: "es_EC", label: "Spanish (ECU)" },
  { value: "es_HN", label: "Spanish (HND)" },
  { value: "es_MX", label: "Spanish (MEX)" },
  { value: "es_PA", label: "Spanish (PAN)" },
  { value: "es_PE", label: "Spanish (PER)" },
  { value: "es_ES", label: "Spanish (SPA)" },
  { value: "es_UY", label: "Spanish (URY)" },
  { value: "sw", label: "Swahili" },
  { value: "sv", label: "Swedish" },
  { value: "ta", label: "Tamil" },
  { value: "te", label: "Telugu" },
  { value: "th", label: "Thai" },
  { value: "tr", label: "Turkish" },
  { value: "uk", label: "Ukrainian" },
  { value: "ur", label: "Urdu" },
  { value: "uz", label: "Uzbek" },
  { value: "vi", label: "Vietnamese" },
  { value: "zu", label: "Zulu" },
];

const HEADER_TYPES = [
  { value: "none", label: "None" },
  { value: "text", label: "Text" },
  { value: "image", label: "Image" },
  { value: "video", label: "Video" },
  { value: "document", label: "Document" },
];

/* Media header formats need a SAMPLE file uploaded to Meta at creation time so
   reviewers can see the message. `accept` mirrors the server-side allow-list in
   utils/whatsappTemplateMedia.js — Meta accepts nothing else as a sample. */
const MEDIA_HEADER_SPECS = {
  image: { format: "IMAGE", accept: "image/jpeg,image/png", hint: "JPG or PNG, up to 5MB" },
  video: { format: "VIDEO", accept: "video/mp4,video/3gpp", hint: "MP4, up to 16MB" },
  document: { format: "DOCUMENT", accept: "application/pdf", hint: "PDF, up to 100MB" },
};

const isMediaHeader = (headerType) => Object.hasOwn(MEDIA_HEADER_SPECS, headerType);

// Button types matching WhatsApp Manager options
const BUTTON_TYPES = [
  // "Custom" is WhatsApp Manager's own name for a QUICK_REPLY. The description
  // spells out what it is for, because this is the ONLY button type that can
  // trigger an LMS action — the others navigate or dial and send nothing back.
  { value: "QUICK_REPLY", label: "Custom (quick reply)", icon: MessageSquare, desc: "Sends a reply back — the only type that can run an action" },
  { value: "URL", label: "Visit website", icon: Globe, desc: "Open a URL" },
  { value: "PHONE_NUMBER", label: "Call Phone Number", icon: Phone, desc: "Dial a phone number" },
  { value: "COPY_CODE", label: "Copy offer code", icon: Copy, desc: "Copy a coupon code" },
];

const EMPTY_FORM = {
  name: "",
  language: "en",
  category: "UTILITY",
  headerType: "none",
  headerText: "",
  /* Uploaded sample for a media header:
     { handle, format, filename, mimeType, previewUrl }. `handle` is what Meta
     needs; `previewUrl` is a local object URL used only for the preview pane. */
  headerMedia: null,
  body: "",
  footer: "",
  buttons: [],
};

// Helper: count positional params in text
const countParams = (text) => (text?.match(/\{\{\d+\}\}/g) || []).length;
const extractParams = (text) => text?.match(/\{\{\d+\}\}/g) || [];

/**
 * Convert a Meta-spec components array (as stored in WhatsAppTemplate /
 * predefined library) back into the editor form state. Returns
 * { form, bodyExamples, headerExamples }.
 */
export const componentsToFormState = (template = {}) => {
  const components = Array.isArray(template.components) ? template.components : [];
  const form = { ...EMPTY_FORM };
  let bodyExamples = [];
  let headerExamples = [];

  if (template.name) form.name = template.name;
  if (template.language) form.language = template.language;
  if (template.category) form.category = String(template.category).toUpperCase();

  for (const c of components) {
    const type = (c.type || "").toUpperCase();
    if (type === "HEADER") {
      const fmt = (c.format || "TEXT").toUpperCase();
      if (fmt === "TEXT") {
        form.headerType = "text";
        form.headerText = c.text || "";
        const cnt = countParams(form.headerText);
        const samples = c.example?.header_text || [];
        headerExamples = Array.from({ length: cnt }, (_, i) => samples[i] || "");
      } else if (["IMAGE", "VIDEO", "DOCUMENT"].includes(fmt)) {
        form.headerType = fmt.toLowerCase();
        /* Carry the existing handle so re-submitting an unchanged template
           doesn't force a pointless re-upload. There is no preview URL — Meta
           never gives the sample back, only the opaque handle. */
        const handle = c.example?.header_handle?.[0] || "";
        form.headerMedia = handle
          ? { handle, format: fmt, filename: "", mimeType: "", previewUrl: "" }
          : null;
      }
    } else if (type === "BODY") {
      form.body = c.text || "";
      const cnt = countParams(form.body);
      const row = c.example?.body_text?.[0] || [];
      bodyExamples = Array.from({ length: cnt }, (_, i) => row[i] || "");
    } else if (type === "FOOTER") {
      form.footer = c.text || "";
    } else if (type === "BUTTONS") {
      const btns = Array.isArray(c.buttons) ? c.buttons : [];
      form.buttons = btns.map((b) => {
        const bt = (b.type || "").toUpperCase();
        if (bt === "URL") {
          const isDynamic = /\{\{\d+\}\}/.test(b.url || "");
          return { type: "URL", text: b.text || "", url: b.url || "", urlType: isDynamic ? "dynamic" : "static" };
        }
        if (bt === "PHONE_NUMBER") {
          return { type: "PHONE_NUMBER", text: b.text || "", phone_number: b.phone_number || "" };
        }
        if (bt === "COPY_CODE") {
          return { type: "COPY_CODE", text: b.text || "", example: b.example || "" };
        }
        return { type: "QUICK_REPLY", text: b.text || "" };
      });
    }
  }

  return { form, bodyExamples, headerExamples };
};

// WhatsApp Manager's own names for the four button types, which is what an admin
// comparing the two screens expects to read.
const BUTTON_TYPE_LABELS = {
  URL: "Visit website",
  PHONE_NUMBER: "Call Phone Number",
  QUICK_REPLY: "Custom",
  COPY_CODE: "Copy offer code",
};

/* ── Local building blocks ───────────────────────────────────────────────── */

/** One of the three cards this form is divided into. */
function Section({ title, optional = false, desc, overflowVisible = false, children }) {
  return (
    <AppCard className="tw-mb-4" overflowVisible={overflowVisible}>
      <AppCardContent overflowVisible={overflowVisible}>
        <h6 className="tw-mb-1 tw-text-[0.92rem] tw-font-semibold tw-text-content">
          {title}
          {optional && <OptionalMark />}
        </h6>
        {desc && (
          <p className="tw-mb-4 tw-text-[0.78rem] tw-leading-normal tw-text-content-muted">{desc}</p>
        )}
        {children}
      </AppCardContent>
    </AppCard>
  );
}

/** The "· Optional" note that follows a heading or a label. */
const OptionalMark = () => (
  <span className="tw-ml-1 tw-font-normal tw-text-content-muted">· Optional</span>
);

/** How much of a field's character budget is spent. Meta rejects a template that
 *  goes over, so the number is part of the field, not a nicety. */
const CharCount = ({ value, max }) => (
  <span className="tw-text-[0.72rem] tw-text-content-subtle">
    {value}/{max}
  </span>
);

/** Appends `{{n}}` to a text field. A link inside a form rather than a button —
 *  it acts on the field above it, it does not submit anything. */
function AddVariableButton({ onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-px-0 tw-py-0.5 tw-text-[0.78rem] tw-font-medium tw-text-primary hover:tw-underline disabled:tw-cursor-default disabled:tw-text-content-subtle disabled:tw-no-underline tw-focus-ring"
    >
      + Add variable
    </button>
  );
}

/** The panel asking for one example value per `{{n}}`, which Meta needs to
 *  review the template. */
function VariableSamples({ part, desc, children }) {
  return (
    <div
      role="group"
      aria-label={`${part} variable samples`}
      className="tw-mb-4 tw-rounded-[6px] tw-border tw-border-solid tw-border-line tw-bg-surface-alt tw-px-[18px] tw-py-3.5"
    >
      <span className="tw-text-[0.82rem] tw-font-semibold tw-text-content">Variable samples</span>
      <p className="tw-mb-3 tw-mt-1 tw-text-[0.75rem] tw-leading-[1.45] tw-text-content-muted">{desc}</p>
      {children}
    </div>
  );
}

/** One `{{n}}` and the sample value for it. */
function SampleRow({ index, children }) {
  return (
    <div className="tw-mb-2 tw-flex tw-items-center tw-gap-2.5 last:tw-mb-0">
      <span className="tw-min-w-[48px] tw-whitespace-nowrap tw-rounded-[6px] tw-border tw-border-solid tw-border-line tw-bg-surface tw-px-2.5 tw-py-[5px] tw-text-center tw-text-[0.78rem] tw-font-medium tw-text-content-muted">
        {`{{${index + 1}}}`}
      </span>
      <span className="tw-min-w-0 tw-flex-1">{children}</span>
    </div>
  );
}

/**
 * What to tell the admin when a request fails. The server's sentence first —
 * it carries Meta's own explanation of what is wrong with the template. Axios's
 * `err.message` is only "Request failed with status code 400", which is what
 * this form used to show for every refusal.
 */
const apiErrorText = (err, what) => {
  const serverMessage = err?.response?.data?.message;
  if (serverMessage) return serverMessage;
  if (err?.response) return `${what} (error ${err.response.status}). Please try again.`;
  return "Could not reach the server. Check your internet connection and try again.";
};

const CreateTemplateForm = ({ onSubmit, onCancel, initialValues = null, submitLabel = "Submit for Review" }) => {
  const initial = useMemo(
    () => (initialValues ? componentsToFormState(initialValues) : { form: { ...EMPTY_FORM }, bodyExamples: [], headerExamples: [] }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [form, setForm] = useState(initial.form);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // The message sits above a long form whose Submit is at the bottom — bring it
  // into view, or a refusal looks like nothing happened.
  const errorRef = useRef(null);
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  }, [error]);
  const [bodyExamples, setBodyExamples] = useState(initial.bodyExamples);
  const [headerExamples, setHeaderExamples] = useState(initial.headerExamples);
  const [showBtnMenu, setShowBtnMenu] = useState(false);
  const [uploadingSample, setUploadingSample] = useState(false);
  const sampleInputRef = useRef(null);
  const btnMenuWrapRef = useRef(null);

  /* Dismiss the "Add button" menu on an outside click or Escape. It floats over
     the actions row below the card, so without this it would sit on top of
     Discard/Submit with no way to get rid of it except picking a button type. */
  useEffect(() => {
    if (!showBtnMenu) return undefined;
    const onPointerDown = (e) => {
      if (!btnMenuWrapRef.current?.contains(e.target)) setShowBtnMenu(false);
    };
    const onKeyDown = (e) => { if (e.key === "Escape") setShowBtnMenu(false); };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [showBtnMenu]);

  /* Object URLs for the local media preview are revoked on unmount — without
     this the blob stays alive for the life of the tab every time the admin
     swaps the sample image. */
  const previewUrlRef = useRef("");
  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  /* ── Upload the header sample to Meta and keep the returned handle ─────────
     The handle — not the file — is what goes into the template. It is obtained
     through Meta's Resumable Upload API, which is a different endpoint from the
     one that produces sendable media ids, so this cannot reuse /upload-media. */
  const handleSamplePicked = async (file) => {
    if (!file) return;
    setUploadingSample(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await apiClient.post("/whatsapp/templates/upload-sample", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      // Only images are worth previewing inline; a PDF or MP4 shows as a chip.
      const previewUrl = file.type.startsWith("image/") ? URL.createObjectURL(file) : "";
      previewUrlRef.current = previewUrl;

      setForm((prev) => ({
        ...prev,
        headerMedia: {
          handle: data.handle,
          format: data.format,
          filename: data.filename || file.name,
          mimeType: data.mimeType || file.type,
          previewUrl,
        },
      }));
    } catch (err) {
      setError(apiErrorText(err, "The sample file could not be uploaded"));
    }
    setUploadingSample(false);
  };

  const clearSample = () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = "";
    setForm((prev) => ({ ...prev, headerMedia: null }));
  };

  // Derived counts
  const bodyParamCount = useMemo(() => countParams(form.body), [form.body]);
  const headerParamCount = useMemo(() => countParams(form.headerText), [form.headerText]);
  const bodyCharCount = form.body.length;

  const update = (key) => (e) => {
    const val = e.target.value;
    setForm((prev) => ({ ...prev, [key]: val }));
    setError("");

    if (key === "body") {
      const cnt = countParams(val);
      setBodyExamples((prev) => {
        const next = [...prev];
        while (next.length < cnt) next.push("");
        return next.slice(0, cnt);
      });
    }
    if (key === "headerText") {
      const cnt = countParams(val);
      setHeaderExamples((prev) => {
        const next = [...prev];
        while (next.length < cnt) next.push("");
        return next.slice(0, cnt);
      });
    }
  };

  // Insert variable into body text at cursor (append)
  const addBodyVariable = () => {
    const nextIdx = bodyParamCount + 1;
    setForm((prev) => ({ ...prev, body: prev.body + `{{${nextIdx}}}` }));
    setBodyExamples((prev) => [...prev, ""]);
  };

  const addHeaderVariable = () => {
    if (headerParamCount >= 1) return; // Header supports max 1 variable
    setForm((prev) => ({ ...prev, headerText: prev.headerText + "{{1}}" }));
    setHeaderExamples([""] );
  };

  // ── Buttons management ────────────────────────────────────────────────────
  const addButton = (type) => {
    if (form.buttons.length >= 10) return;
    const base = { text: "" };
    if (type === "URL") Object.assign(base, { type: "URL", url: "", urlType: "static" });
    else if (type === "PHONE_NUMBER") Object.assign(base, { type: "PHONE_NUMBER", phone_number: "" });
    else if (type === "COPY_CODE") Object.assign(base, { type: "COPY_CODE", example: "" });
    else Object.assign(base, { type: "QUICK_REPLY" });

    setForm((prev) => ({ ...prev, buttons: [...prev.buttons, base] }));
  };

  const updateButton = (idx, field, value) => {
    setForm((prev) => {
      const buttons = [...prev.buttons];
      buttons[idx] = { ...buttons[idx], [field]: value };
      return { ...prev, buttons };
    });
  };

  const removeButton = (idx) => {
    setForm((prev) => ({
      ...prev,
      buttons: prev.buttons.filter((_, i) => i !== idx),
    }));
  };

  // ── Build Meta components array ───────────────────────────────────────────
  const buildComponents = () => {
    const components = [];

    // Header
    if (form.headerType === "text" && form.headerText.trim()) {
      const hdr = { type: "HEADER", format: "TEXT", text: form.headerText.trim() };
      if (headerParamCount > 0) {
        hdr.example = {
          header_text: headerExamples.map((v) => v.trim() || "example"),
        };
      }
      components.push(hdr);
    } else if (isMediaHeader(form.headerType) && form.headerMedia?.handle) {
      // A media header carries no text — only the reviewed sample.
      components.push({
        type: "HEADER",
        format: MEDIA_HEADER_SPECS[form.headerType].format,
        example: { header_handle: [form.headerMedia.handle] },
      });
    }

    if (!form.body.trim()) return null;

    // Body
    const bodyComp = { type: "BODY", text: form.body.trim() };
    if (bodyParamCount > 0) {
      bodyComp.example = {
        body_text: [bodyExamples.map((v) => v.trim() || "example")],
      };
    }
    components.push(bodyComp);

    // Footer
    if (form.footer.trim()) {
      components.push({ type: "FOOTER", text: form.footer.trim() });
    }

    // Buttons
    if (form.buttons.length > 0) {
      const btns = form.buttons.map((b) => {
        if (b.type === "URL") {
          const btn = { type: "URL", text: b.text, url: b.url };
          if (b.urlType === "dynamic" && b.url) {
            btn.example = [b.url];
          }
          return btn;
        }
        if (b.type === "PHONE_NUMBER") return { type: "PHONE_NUMBER", text: b.text, phone_number: b.phone_number };
        if (b.type === "COPY_CODE") return { type: "COPY_CODE", example: b.example || "DISCOUNT10" };
        return { type: "QUICK_REPLY", text: b.text };
      });
      components.push({ type: "BUTTONS", buttons: btns });
    }

    return components;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.name.trim()) { setError("Template name is required"); return; }
    if (!/^[a-z][a-z0-9_]*$/.test(form.name.trim())) {
      setError("Name must be lowercase, start with a letter, only letters/numbers/underscores");
      return;
    }
    if (!form.body.trim()) { setError("Body text is required"); return; }

    // A media header without its sample is rejected by Meta at submission, so
    // catch it here where the fix is one click away.
    if (isMediaHeader(form.headerType) && !form.headerMedia?.handle) {
      setError(`Upload a sample ${form.headerType} for the header before submitting`);
      return;
    }
    if (uploadingSample) { setError("Wait for the sample upload to finish"); return; }

    // Validate body examples
    if (bodyParamCount > 0) {
      const missing = bodyExamples.slice(0, bodyParamCount).some((v) => !v?.trim());
      if (missing || bodyExamples.length < bodyParamCount) {
        setError("Sample values are required for all body variables");
        return;
      }
    }

    // Validate header examples
    if (headerParamCount > 0) {
      const missing = headerExamples.slice(0, headerParamCount).some((v) => !v?.trim());
      if (missing || headerExamples.length < headerParamCount) {
        setError("Sample values are required for header variables");
        return;
      }
    }

    const components = buildComponents();
    if (!components) { setError("Body text is required"); return; }

    setSubmitting(true);
    try {
      await onSubmit({
        name: form.name.trim(),
        language: form.language,
        category: form.category,
        components,
      });
      setForm({ ...EMPTY_FORM });
      setBodyExamples([]);
      setHeaderExamples([]);
    } catch (err) {
      setError(apiErrorText(err, "The template could not be submitted"));
    }
    setSubmitting(false);
  };

  // ── Preview text with highlights ──────────────────────────────────────────
  const previewBody = useMemo(() => {
    let text = form.body;
    const params = extractParams(text);
    params.forEach((p, i) => {
      const example = bodyExamples[i]?.trim();
      if (example) {
        text = text.replace(p, example);
      }
    });
    return text;
  }, [form.body, bodyExamples]);

  const previewHeader = useMemo(() => {
    let text = form.headerText;
    const params = extractParams(text);
    params.forEach((p, i) => {
      const example = headerExamples[i]?.trim();
      if (example) {
        text = text.replace(p, example);
      }
    });
    return text;
  }, [form.headerText, headerExamples]);

  return (
    <div className="tw-grid tw-grid-cols-[1fr_320px] tw-items-start tw-gap-6 max-[991px]:tw-grid-cols-1">
      <form onSubmit={handleSubmit} className="tw-min-w-0">
        {error && (
          <div
            ref={errorRef}
            role="alert"
            className="tw-mb-4 tw-flex tw-items-center tw-gap-2 tw-rounded-[6px] tw-border tw-border-solid tw-border-warning/40 tw-bg-warning-soft tw-px-3.5 tw-py-2.5 tw-text-[0.82rem] tw-text-warning-fg"
          >
            <AlertTriangle size={14} className="tw-shrink-0" aria-hidden="true" /> {error}
          </div>
        )}

        {/* ── Section: Template name and language ─────────────────────── */}
        <Section title="Template name and language">
          <Row>
            <Col md={6}>
              <FormField label="Name your template" className="tw-mb-4">
                <Input
                  type="text"
                  placeholder="e.g. welcome_message"
                  value={form.name}
                  onChange={update("name")}
                  disabled={submitting}
                  maxLength={512}
                  trailing={<CharCount value={form.name.length} max={512} />}
                  trailingPad="text"
                />
              </FormField>
            </Col>
            <Col md={3}>
              <FormField label="Select language" className="tw-mb-4">
                <NativeSelect value={form.language} onChange={update("language")} disabled={submitting}>
                  {LANGUAGES.map((l) => (
                    <option key={l.value} value={l.value}>{l.label}</option>
                  ))}
                </NativeSelect>
              </FormField>
            </Col>
            <Col md={3}>
              <FormField label="Category" className="tw-mb-4">
                <NativeSelect value={form.category} onChange={update("category")} disabled={submitting}>
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </NativeSelect>
              </FormField>
            </Col>
          </Row>
        </Section>

        {/* ── Section: Content ────────────────────────────────────────── */}
        <Section
          title="Content"
          desc="Add a header, body and footer for your template. Cloud API hosted by Meta will review the template variables and content to protect the security and integrity of our services."
        >
          {/* Header */}
          <div className="tw-mb-5 last:tw-mb-0">
            <Label htmlFor="template-header-type">
              Header <OptionalMark />
            </Label>
            <Row className="tw-mb-2 tw-mt-1.5">
              <Col md={3}>
                <NativeSelect
                  id="template-header-type"
                  value={form.headerType}
                  onChange={update("headerType")}
                  disabled={submitting}
                >
                  {HEADER_TYPES.map((h) => (
                    <option key={h.value} value={h.value}>{h.label}</option>
                  ))}
                </NativeSelect>
              </Col>
            </Row>
            {isMediaHeader(form.headerType) && (
              <div className="tw-mb-2">
                <input
                  ref={sampleInputRef}
                  type="file"
                  accept={MEDIA_HEADER_SPECS[form.headerType].accept}
                  className="tw-hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    handleSamplePicked(f);
                  }}
                />

                {form.headerMedia?.handle ? (
                  <div className="tw-flex tw-items-center tw-gap-2.5 tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-px-2.5 tw-py-2">
                    {form.headerMedia.previewUrl ? (
                      <img
                        src={form.headerMedia.previewUrl}
                        alt="Header sample"
                        className="tw-h-10 tw-w-10 tw-shrink-0 tw-rounded-[5px] tw-object-cover"
                      />
                    ) : (
                      <span className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[5px] tw-bg-surface-sunken tw-text-content-muted">
                        {form.headerType === "video" ? <Video size={18} /> : <FileText size={18} />}
                      </span>
                    )}
                    <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
                      <span className="tw-truncate tw-text-[0.8rem] tw-text-content">
                        {form.headerMedia.filename || `${MEDIA_HEADER_SPECS[form.headerType].format} sample`}
                      </span>
                      <span className="tw-text-[0.72rem] tw-text-success-fg">Sample uploaded</span>
                    </span>
                    <IconButton
                      icon={X}
                      label="Remove sample"
                      variant="ghost"
                      size="icon-sm"
                      onClick={clearSample}
                      disabled={submitting || uploadingSample}
                    />
                  </div>
                ) : (
                  <DropZone
                    size="sm"
                    onClick={() => sampleInputRef.current?.click()}
                    disabled={submitting || uploadingSample}
                    className="tw-text-[0.8rem] tw-text-content-muted"
                  >
                    {uploadingSample ? (
                      <><Spinner animation="border" size="sm" /> Uploading sample…</>
                    ) : (
                      <>
                        {form.headerType === "image" ? <ImageIcon size={16} aria-hidden="true" />
                          : form.headerType === "video" ? <Video size={16} aria-hidden="true" />
                          : <FileText size={16} aria-hidden="true" />}
                        <span>Upload sample {form.headerType}</span>
                      </>
                    )}
                  </DropZone>
                )}

                <p className="tw-m-0 tw-mt-1.5 tw-flex tw-items-center tw-text-[0.72rem] tw-leading-[1.4] tw-text-content-subtle">
                  <Upload size={12} className="tw-mr-1 tw-shrink-0" aria-hidden="true" />
                  {MEDIA_HEADER_SPECS[form.headerType].hint}. Meta reviews this sample —
                  each message you send later supplies its own {form.headerType}.
                </p>
              </div>
            )}
            {form.headerType === "text" && (
              <>
                <Input
                  type="text"
                  placeholder="Add a short line of text to the header of your message"
                  value={form.headerText}
                  onChange={update("headerText")}
                  maxLength={60}
                  disabled={submitting}
                  aria-label="Header text"
                  trailing={<CharCount value={form.headerText.length} max={60} />}
                  trailingPad="text"
                />
                <div className="tw-mt-1 tw-flex tw-justify-end">
                  {headerParamCount < 1 && (
                    <AddVariableButton onClick={addHeaderVariable} disabled={submitting} />
                  )}
                </div>
                {/* Header variable samples */}
                {headerParamCount > 0 && (
                  <VariableSamples part="Header" desc="Include samples of all variables in your message to help Meta review your template.">
                    <span className="tw-mb-1 tw-block tw-text-[0.8125rem] tw-font-semibold tw-text-content">Header</span>
                    {Array.from({ length: headerParamCount }, (_, i) => (
                      <SampleRow key={`hdr-${i}`} index={i}>
                        <Input
                          type="text"
                          placeholder={`Enter content for {{${i + 1}}}`}
                          value={headerExamples[i] || ""}
                          aria-label={`Sample for header variable ${i + 1}`}
                          onChange={(e) => {
                            const val = e.target.value;
                            setHeaderExamples((prev) => {
                              const next = [...prev];
                              next[i] = val;
                              return next;
                            });
                          }}
                          disabled={submitting}
                        />
                      </SampleRow>
                    ))}
                  </VariableSamples>
                )}
              </>
            )}
          </div>

          {/* Body */}
          <div className="tw-mb-5 last:tw-mb-0">
            <Label htmlFor="template-body" required>Body</Label>
            <Textarea
              id="template-body"
              rows={5}
              placeholder={"Hello {{1}},\n\nYour class starts at {{2}}.\n\nThank you!"}
              value={form.body}
              onChange={update("body")}
              maxLength={1024}
              disabled={submitting}
              className="tw-mt-1.5"
            />
            <div className="tw-mt-1 tw-flex tw-items-center tw-justify-between">
              <AddVariableButton onClick={addBodyVariable} disabled={submitting} />
              <CharCount value={bodyCharCount} max={1024} />
            </div>
          </div>

          {/* Body variable samples */}
          {bodyParamCount > 0 && (
            <VariableSamples part="Body" desc="Include samples of all variables in your message to help Meta review your template. Remember not to include any customer information to protect your customer's privacy.">
              <span className="tw-mb-1 tw-block tw-text-[0.8125rem] tw-font-semibold tw-text-content">Body</span>
              {Array.from({ length: bodyParamCount }, (_, i) => (
                <SampleRow key={`body-${i}`} index={i}>
                  <Input
                    type="text"
                    placeholder={`Enter content for {{${i + 1}}}`}
                    value={bodyExamples[i] || ""}
                    aria-label={`Sample for body variable ${i + 1}`}
                    onChange={(e) => {
                      const val = e.target.value;
                      setBodyExamples((prev) => {
                        const next = [...prev];
                        next[i] = val;
                        return next;
                      });
                    }}
                    disabled={submitting}
                  />
                </SampleRow>
              ))}
            </VariableSamples>
          )}

          {/* Footer */}
          <div className="tw-mb-5 last:tw-mb-0">
            <FormField label={<>Footer <OptionalMark /></>}>
              <Input
                type="text"
                placeholder="Add a short line of text to the bottom of your message"
                value={form.footer}
                onChange={update("footer")}
                maxLength={60}
                disabled={submitting}
                trailing={<CharCount value={form.footer.length} max={60} />}
                trailingPad="text"
              />
            </FormField>
          </div>
        </Section>

        {/* ── Section: Buttons ────────────────────────────────────────── */}
        {/* `overflowVisible`: the "Add button" menu is absolutely positioned
            inside this card, and AppCard clips its content by default. */}
        <Section
          title="Buttons"
          optional
          overflowVisible
          desc="Create buttons that let customers respond to your message or take action. If you add more than three buttons, they will appear in a list."
        >
          {/* Existing buttons */}
          {form.buttons.map((btn, idx) => (
            <div
              key={`btn-${idx}`}
              className="tw-mb-2.5 tw-overflow-hidden tw-rounded-[6px] tw-border tw-border-solid tw-border-line"
            >
              <div className="tw-flex tw-items-center tw-justify-between tw-border-0 tw-border-b tw-border-solid tw-border-line tw-bg-surface-alt tw-px-3.5 tw-py-2">
                <span className="tw-text-[0.78rem] tw-font-semibold tw-text-content">
                  {BUTTON_TYPE_LABELS[btn.type]}
                </span>
                <IconButton
                  icon={X}
                  label={`Remove ${BUTTON_TYPE_LABELS[btn.type] || "button"}`}
                  variant="ghost-destructive"
                  size="icon-sm"
                  onClick={() => removeButton(idx)}
                />
              </div>
              <div className="tw-px-3.5 tw-py-3">
                {btn.type === "QUICK_REPLY" && (
                  <Row>
                    <Col md={6}>
                      <FormField label="Button text" labelTone="muted">
                        <Input
                          type="text"
                          placeholder="Button text"
                          value={btn.text}
                          onChange={(e) => updateButton(idx, "text", e.target.value)}
                          maxLength={25}
                          disabled={submitting}
                          trailing={<CharCount value={(btn.text || "").length} max={25} />}
                          trailingPad="text"
                        />
                      </FormField>
                    </Col>
                  </Row>
                )}

                {btn.type === "URL" && (
                  <Row className="tw-items-end">
                    <Col md={4}>
                      <FormField label="Button text" labelTone="muted">
                        <Input
                          type="text"
                          placeholder="Visit website"
                          value={btn.text}
                          onChange={(e) => updateButton(idx, "text", e.target.value)}
                          maxLength={25}
                          disabled={submitting}
                          trailing={<CharCount value={(btn.text || "").length} max={25} />}
                          trailingPad="text"
                        />
                      </FormField>
                    </Col>
                    <Col md={2}>
                      <FormField label="URL type" labelTone="muted">
                        <NativeSelect
                          value={btn.urlType || "static"}
                          onChange={(e) => updateButton(idx, "urlType", e.target.value)}
                          disabled={submitting}
                          options={[
                            { value: "static", label: "Static" },
                            { value: "dynamic", label: "Dynamic" },
                          ]}
                        />
                      </FormField>
                    </Col>
                    <Col md={6}>
                      <FormField label="Website URL" labelTone="muted">
                        <Input
                          type="text"
                          placeholder="https://www.example.com"
                          value={btn.url}
                          onChange={(e) => updateButton(idx, "url", e.target.value)}
                          maxLength={2000}
                          disabled={submitting}
                          trailing={<CharCount value={(btn.url || "").length} max={2000} />}
                          trailingPad="text"
                        />
                      </FormField>
                    </Col>
                  </Row>
                )}

                {btn.type === "PHONE_NUMBER" && (
                  <Row>
                    <Col md={5}>
                      <FormField label="Button text" labelTone="muted">
                        <Input
                          type="text"
                          placeholder="Call us"
                          value={btn.text}
                          onChange={(e) => updateButton(idx, "text", e.target.value)}
                          maxLength={25}
                          disabled={submitting}
                          trailing={<CharCount value={(btn.text || "").length} max={25} />}
                          trailingPad="text"
                        />
                      </FormField>
                    </Col>
                    <Col md={5}>
                      <FormField label="Phone number" labelTone="muted">
                        <Input
                          type="text"
                          placeholder="+91 9876543210"
                          value={btn.phone_number}
                          onChange={(e) => updateButton(idx, "phone_number", e.target.value)}
                          disabled={submitting}
                        />
                      </FormField>
                    </Col>
                  </Row>
                )}

                {btn.type === "COPY_CODE" && (
                  <Row>
                    <Col md={6}>
                      <FormField label="Offer code example" labelTone="muted">
                        <Input
                          type="text"
                          placeholder="e.g. DISCOUNT10"
                          value={btn.example || ""}
                          onChange={(e) => updateButton(idx, "example", e.target.value)}
                          disabled={submitting}
                        />
                      </FormField>
                    </Col>
                  </Row>
                )}
              </div>
            </div>
          ))}

          {/* Add button menu */}
          {form.buttons.length < 10 && (
            <div className="tw-relative tw-inline-block" ref={btnMenuWrapRef}>
              <AppButton
                type="button"
                variant="outline"
                size="sm"
                aria-haspopup="menu"
                aria-expanded={showBtnMenu}
                onClick={() => setShowBtnMenu(!showBtnMenu)}
              >
                <Plus size={14} /> Add button <ChevronDown size={14} />
              </AppButton>
              {showBtnMenu && (
                <div
                  role="menu"
                  className="tw-absolute tw-left-0 tw-top-[calc(100%+4px)] tw-z-10 tw-min-w-[220px] tw-animate-drop-in tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-p-1 tw-shadow-popover"
                >
                  {BUTTON_TYPES.map((bt) => {
                    const Icon = bt.icon;
                    return (
                      <button
                        key={bt.value}
                        type="button"
                        role="menuitem"
                        onClick={() => { addButton(bt.value); setShowBtnMenu(false); }}
                        className="tw-flex tw-w-full tw-cursor-pointer tw-items-start tw-gap-2.5 tw-rounded-[6px] tw-border-0 tw-bg-transparent tw-px-3 tw-py-2 tw-text-left tw-text-content tw-transition-colors hover:tw-bg-surface-alt tw-focus-ring-inset"
                      >
                        <Icon size={14} className="tw-mt-0.5 tw-shrink-0 tw-text-content-muted" aria-hidden="true" />
                        <span className="tw-block">
                          <span className="tw-block tw-text-[0.82rem] tw-font-medium tw-text-content">{bt.label}</span>
                          <span className="tw-mt-px tw-block tw-text-[0.72rem] tw-text-content-subtle">{bt.desc}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </Section>

        {/* ── Actions ─────────────────────────────────────────────────── */}
        <div className="tw-flex tw-items-center tw-justify-end tw-gap-2.5 tw-pb-2 tw-pt-4">
          {onCancel && (
            <AppButton type="button" variant="outline" size="sm" onClick={onCancel} disabled={submitting}>
              Discard
            </AppButton>
          )}
          <AppButton type="submit" size="sm" disabled={submitting}>
            {submitting ? <><Spinner animation="border" size="sm" /> Submitting...</> : submitLabel}
          </AppButton>
        </div>
      </form>

      {/* ── Template Preview (right side) ─────────────────────────────── */}
      <div className="tw-sticky tw-top-5">
        <h6 className="tw-mb-3 tw-text-[0.82rem] tw-font-semibold tw-text-content">Template preview</h6>
        <TemplatePreview
          headerType={form.headerType}
          headerText={previewHeader}
          media={form.headerMedia}
          body={previewBody}
          footer={form.footer}
          buttons={form.buttons}
        />
      </div>
    </div>
  );
};

export default CreateTemplateForm;
